import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

const VIEWPORT_HEIGHT = 220;

export const ADD_MEMBER_SPECIAL_NOTES_PLACEHOLDER_TITLE =
  "Suggested info to add to this field:";

export const ADD_MEMBER_SPECIAL_NOTES_PLACEHOLDER_LINES = [
  "Nick name",
  "Education, degree(s)",
  "# of Marriages and names for each",
  "Health conditions",
  "Company Name",
  "Job Title",
  "Work Status — retired, working FT, working PT, etc.",
  "# of children",
  "Special Talents, etc.",
];

export const ADD_MEMBER_SPECIAL_NOTES_PLACEHOLDER = [
  ADD_MEMBER_SPECIAL_NOTES_PLACEHOLDER_TITLE,
  ...ADD_MEMBER_SPECIAL_NOTES_PLACEHOLDER_LINES,
].join("\n");

/**
 * Split full text so the left column fits within maxHeight (measured off-DOM).
 */
function splitTextForColumns(fullText, measurer, maxHeight) {
  if (!fullText) return ["", ""];
  if (maxHeight <= 0) return [fullText, ""];

  measurer.textContent = fullText;
  if (measurer.scrollHeight <= maxHeight) {
    return [fullText, ""];
  }

  let low = 0;
  let high = fullText.length;

  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    measurer.textContent = fullText.slice(0, mid);
    if (measurer.scrollHeight <= maxHeight) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }

  let splitAt = low;
  const newline = fullText.lastIndexOf("\n", low);
  if (newline > 0 && newline >= low - 40) {
    splitAt = newline + 1;
    measurer.textContent = fullText.slice(0, splitAt);
    if (measurer.scrollHeight > maxHeight) {
      splitAt = low;
    }
  }

  return [fullText.slice(0, splitAt), fullText.slice(splitAt)];
}

/**
 * Special Notes — two columns (fill left, then right); right column scrolls vertically.
 */
export function AddMemberSpecialNotesField({
  value,
  onChange,
  name = "note",
  disabled = false,
}) {
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const rightScrollRef = useRef(null);
  const measurerRef = useRef(null);
  const isInternalChange = useRef(false);

  const getMaxColumnHeight = useCallback(() => {
    const leftEl = leftRef.current;
    if (!leftEl) return VIEWPORT_HEIGHT - 20;
    return leftEl.clientHeight || VIEWPORT_HEIGHT - 20;
  }, []);

  const syncMeasurerWidth = useCallback(() => {
    const measurer = measurerRef.current;
    const leftEl = leftRef.current;
    if (!measurer || !leftEl) return;
    measurer.style.width = `${leftEl.clientWidth}px`;
  }, []);

  const applyColumnSplit = useCallback(
    (fullText) => {
      const leftEl = leftRef.current;
      const rightEl = rightRef.current;
      const measurer = measurerRef.current;
      if (!leftEl || !rightEl || !measurer) return;

      syncMeasurerWidth();
      const [left, right] = splitTextForColumns(
        fullText,
        measurer,
        getMaxColumnHeight()
      );

      if (leftEl.innerText !== left) leftEl.innerText = left;
      if (rightEl.innerText !== right) rightEl.innerText = right;
    },
    [getMaxColumnHeight, syncMeasurerWidth]
  );

  const getCombinedText = useCallback(() => {
    const left = leftRef.current?.innerText ?? "";
    const right = rightRef.current?.innerText ?? "";
    return left + right;
  }, []);

  const emitChange = useCallback(
    (text) => {
      isInternalChange.current = true;
      onChange({ target: { name, value: text } });
      requestAnimationFrame(() => {
        isInternalChange.current = false;
      });
    },
    [name, onChange]
  );

  const rebalanceAndEmit = useCallback(() => {
    const combined = getCombinedText();
    applyColumnSplit(combined);
    emitChange(combined);
  }, [applyColumnSplit, emitChange, getCombinedText]);

  const scrollCaretIntoView = useCallback(() => {
    const rightScroll = rightScrollRef.current;
    const rightEl = rightRef.current;
    if (!rightScroll || !rightEl) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    if (!rightEl.contains(selection.anchorNode)) return;

    const range = selection.getRangeAt(0);
    const caretRect = range.getBoundingClientRect();
    const scrollRect = rightScroll.getBoundingClientRect();

    if (caretRect.bottom > scrollRect.bottom - 4) {
      rightScroll.scrollTop += caretRect.bottom - scrollRect.bottom + 12;
    } else if (caretRect.top < scrollRect.top + 4) {
      rightScroll.scrollTop -= scrollRect.top - caretRect.top + 12;
    }
  }, []);

  useLayoutEffect(() => {
    if (isInternalChange.current) return;
    applyColumnSplit(value || "");
  }, [value, applyColumnSplit]);

  useEffect(() => {
    const leftEl = leftRef.current;
    if (!leftEl) return;

    const observer = new ResizeObserver(() => {
      if (isInternalChange.current) return;
      applyColumnSplit(value || getCombinedText());
    });

    observer.observe(leftEl);
    return () => observer.disconnect();
  }, [value, applyColumnSplit, getCombinedText]);

  const handleLeftInput = () => {
    rebalanceAndEmit();
  };

  const handleRightInput = () => {
    rebalanceAndEmit();
    requestAnimationFrame(scrollCaretIntoView);
  };

  const isCaretAtEnd = (el) => {
    const selection = window.getSelection();
    if (!selection?.isCollapsed || !el.contains(selection.anchorNode)) return false;
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const test = range.cloneRange();
    test.setStart(selection.anchorNode, selection.anchorOffset);
    test.setEnd(range.endContainer, range.endOffset);
    return test.toString().length === 0;
  };

  const handleKeyDown = (e, column) => {
    if (e.key === "Tab" && !e.shiftKey && column === "left") {
      e.preventDefault();
      rightRef.current?.focus();
      return;
    }
    if (e.key === "Tab" && e.shiftKey && column === "right") {
      e.preventDefault();
      leftRef.current?.focus();
      return;
    }
    if (e.key === "ArrowRight" && column === "left" && isCaretAtEnd(leftRef.current)) {
      e.preventDefault();
      rightRef.current?.focus();
    }
    if (e.key === "ArrowLeft" && column === "right") {
      const selection = window.getSelection();
      if (selection?.isCollapsed && selection.anchorOffset === 0) {
        e.preventDefault();
        leftRef.current?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
    requestAnimationFrame(() => {
      rebalanceAndEmit();
      scrollCaretIntoView();
    });
  };

  const handleBlur = () => {
    emitChange(getCombinedText().trim());
  };

  const showPlaceholder = !(value || "").length;

  return (
    <div
      className={`addmember-special-notes-editor-wrap${
        disabled ? " addmember-special-notes-editor-wrap--disabled" : ""
      }${showPlaceholder ? " addmember-special-notes-editor-wrap--empty" : ""}`}
    >
      {showPlaceholder && (
        <div className="addmember-special-notes-placeholder" aria-hidden="true">
          <div className="addmember-special-notes-placeholder-title">
            {ADD_MEMBER_SPECIAL_NOTES_PLACEHOLDER_TITLE}
          </div>
          {ADD_MEMBER_SPECIAL_NOTES_PLACEHOLDER_LINES.map((line) => (
            <div key={line} className="addmember-special-notes-placeholder-line">
              {line}
            </div>
          ))}
        </div>
      )}
      <div className="addmember-special-notes-columns">
        <div
          ref={leftRef}
          className="addmember-special-notes-col addmember-special-notes-col--left"
          contentEditable={!disabled}
          role="textbox"
          aria-label="Special notes, first column"
          aria-multiline="true"
          aria-disabled={disabled}
          onInput={handleLeftInput}
          onKeyDown={(e) => handleKeyDown(e, "left")}
          onPaste={handlePaste}
          onBlur={handleBlur}
          suppressContentEditableWarning
        />
        <div
          ref={rightScrollRef}
          className="addmember-special-notes-col-scroll"
        >
          <div
            ref={rightRef}
            className="addmember-special-notes-col addmember-special-notes-col--right"
            contentEditable={!disabled}
            role="textbox"
            aria-label="Special notes, second column"
            aria-multiline="true"
            aria-disabled={disabled}
            onInput={handleRightInput}
            onKeyDown={(e) => handleKeyDown(e, "right")}
            onPaste={handlePaste}
            onBlur={handleBlur}
            suppressContentEditableWarning
          />
        </div>
      </div>
      <div
        ref={measurerRef}
        className="addmember-special-notes-measurer"
        aria-hidden="true"
      />
    </div>
  );
}
